<template>
  <RevContextualMenu class="z-10" position="bottom-end">
    <template #trigger="{ show }">
      <RevButtonIcon
        :alternative-text="i18n(translations.actions)"
        class="ml-12"
        data-qa="actions-menu"
        :icon="IconMoreVertical"
        variant="secondary"
        @click="show"
      />
    </template>

    <template #items>
      <!-- Order actions -->
      <RevListItemInteractive
        :data-qa="'more-details-' + order.id"
        @click="handleMoreDetails"
      >
        <template #label>
          {{ i18n(translations.moreDetails) }}
        </template>
      </RevListItemInteractive>

      <template v-if="!FF_BO_SELLER_IS_REDUCE_SELLER_TO_CONTACTS_AGENT_OPEN">
        <RevListItemInteractive
          v-if="
            hasRouteAccess(
              {
                name: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
                // Fake customerRequestId param, required by hasRouteAccess to be able to resolve the route.
                params: { customerRequestId: '1234' },
              },
              PERMISSION_TYPES.EDIT,
            )
          "
          :data-qa="'contact-customer-' + order.id"
          @click="openModal(CONTACT_CUSTOMER_DRAWER_MODAL_NAME + order.id)"
        >
          <template #label>
            {{ i18n(translations.contactCustomer) }}
          </template>
        </RevListItemInteractive>

        <RevDrawer
          data-qa="contact-customer-drawer"
          :name="CONTACT_CUSTOMER_DRAWER_MODAL_NAME + order.id"
          :title="i18n(translations.contactCustomer)"
        >
          <template #body><ContactCustomer :order /></template>
        </RevDrawer>
      </template>

      <template v-else>
        <RevListItemInteractive
          :data-qa="'fix-order-issue-' + order.id"
          @click="handleFixOrderIssue"
        >
          <template #label>
            {{ i18n(translations.fixOrderIssue) }}
          </template>
        </RevListItemInteractive>

        <FixOrderIssueDrawer :order :refreshOrders />
      </template>
      <OrderRecapDrawer :order />

      <!-- OrderLines actions -->
      <template v-for="orderLine in order.orderLines" :key="orderLine.id">
        <RevListItemInteractive
          v-if="['NEW', 'PAID'].includes(orderLine.state.code)"
          :data-qa="'cancel-order-line-' + orderLine.id"
          @click="handleCancelOrderline(orderLine.id)"
        >
          <template #label>
            {{
              order.orderLines.length > 1
                ? i18n(translations.cancelOrderLine, {
                    orderLineId: orderLine.id,
                  })
                : i18n(translations.cancelOrder)
            }}
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive
          v-if="'VALIDATED' === orderLine.state.code"
          :data-qa="'refund-order-line-' + orderLine.id"
          @click="handleRefundOrderline(orderLine.id)"
        >
          <template #label>
            {{
              order.orderLines.length > 1
                ? i18n(translations.refundOrderLine, {
                    orderLineId: orderLine.id,
                  })
                : i18n(translations.refundOrder)
            }}
          </template>
        </RevListItemInteractive>

        <CancelOrderLineDrawer :order :orderLine :refreshOrders />
        <RefundOrderLineDrawer :order :orderLine :refreshOrders />
      </template>
    </template>
  </RevContextualMenu>
</template>

<script setup lang="ts">
import { usePermissions, useRuntimeConfig } from '#imports'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevContextualMenu } from '@ds/components/ContextualMenu'
import { RevDrawer } from '@ds/components/Drawer'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { openModal } from '@ds/components/ModalBase'
import { IconMoreVertical } from '@ds/icons/IconMoreVertical'

import { PERMISSION_TYPES } from '~/constants/permissions'
import { ROUTE_NAMES } from '~/constants/routes'

import type { Order } from '../../../../../../Tab.types'
import { useOrderRecapDrawer } from '../../../../composables/useOrderRecapDrawer'

import { CONTACT_CUSTOMER_DRAWER_MODAL_NAME } from './MoreActions.constants'
import translations from './MoreActions.translations'
import { MODAL_NAME as CANCEL_ORDERLINE_DRAWER } from './components/CancelOrderLineDrawer/CancelOrderLineDrawer.constants'
import CancelOrderLineDrawer from './components/CancelOrderLineDrawer/CancelOrderLineDrawer.vue'
import { MODAL_NAME as FIX_ORDER_ISSUE_DRAWER } from './components/FixOrderIssueDrawer/FixOrderIssueDrawer.constants'
import FixOrderIssueDrawer from './components/FixOrderIssueDrawer/FixOrderIssueDrawer.vue'
import ContactCustomer from './components/FixOrderIssueDrawer/components/ContactCustomer/ContactCustomer.vue'
import OrderRecapDrawer from './components/OrderRecapDrawer/OrderRecapDrawer.vue'
import { MODAL_NAME as REFUND_ORDERLINE_DRAWER } from './components/RefundOrderLineDrawer/RefundOrderLineDrawer.constants'
import RefundOrderLineDrawer from './components/RefundOrderLineDrawer/RefundOrderLineDrawer.vue'

const { FF_BO_SELLER_IS_REDUCE_SELLER_TO_CONTACTS_AGENT_OPEN } =
  useRuntimeConfig().public
const i18n = useI18n()
const { openOrderDetails } = useOrderRecapDrawer()
const { hasRouteAccess } = usePermissions()
const props = defineProps<{ order: Order; refreshOrders: () => void }>()

async function handleMoreDetails() {
  await openOrderDetails(props.order.id)
}

function handleCancelOrderline(orderLineId: number) {
  openModal(CANCEL_ORDERLINE_DRAWER + orderLineId)
}

function handleRefundOrderline(orderLineId: number) {
  openModal(REFUND_ORDERLINE_DRAWER + orderLineId)
}

function handleFixOrderIssue() {
  openModal(FIX_ORDER_ISSUE_DRAWER + props.order.id)
}
</script>
